/**
 * Body commands (FACE-EDGE KERNEL K1 — docs/bim-plan/21 §3.3/§4) — the element-
 * identity orchestration over the headless K0 core (src/body/):
 *
 * - drawBodySegments: THE write path for every drawing tool (pencil Line,
 *   Rectangle; later Circle/push-pull edges). STICKINESS: segments that come
 *   within the weld tolerance of existing bodies merge those bodies into one
 *   (the largest keeps its id — the splitWall model inverted), then the segments
 *   arrange into it. Segments touching nothing mint a fresh body. One call = one
 *   transaction = one undo step (the pencil calls per-click, so undo walks back
 *   edge by edge — SketchUp exactly).
 * - eraseBodyEdge / eraseBodyFace: SketchUp erase semantics via K0; erasing the
 *   last connecting edge SPLITS the body (largest component keeps the id).
 *
 * The topology param is plain JSON in Yjs — cloned on read, replaced atomically
 * on write (spike §6.1's recorded v1 granularity; the model-wide edit lock makes
 * same-body concurrency impossible by construction, Q11).
 */

import * as Y from 'yjs'
import type { ModelDoc } from '../doc'
import { getElement, listElements, listTypes, maps, recordToYMap, transact } from '../doc'
import { newElementId } from '../ids'
import type { ElementId, LevelId, PhaseId, TypeId } from '../types'
import { createType } from './library'
import type { BodyTopology, BodyPt3, BodyEid, BodyFid } from '../body/types'
import { insertSegment, eraseEdge, eraseFace } from '../body/arrange'
import { extrudeFace, faceAt } from '../body/extrude'
import { componentsOf, mergeTopologies, createTopology } from '../body/topo'
import { transformVertices, copyEdges, verticesOf, edgesOf } from '../body/transform'
import { planeFromPoints } from '../body/geom3'
import { paintFaceSide, reverseFaceInPlace } from '../body/paint'
import { invariants } from '../body/topo'
import { faceOffsetSegments, chainOffsetSegments, insertSegments, followMe } from '../body/offsetFollow'
import { WELD_TOL, pointSegDist, segSegClosest } from '../body/geom3'

/** hard per-body entity ceiling (spike §6.1, Q10 confirmed) — the warn tier (~2k)
 *  is a UI concern; the kernel refuses outright at the cap */
export const BODY_MAX_ENTITIES = 10_000

const clone = <T,>(v: T): T => JSON.parse(JSON.stringify(v)) as T

function topologyOf(rec: { params: Record<string, unknown> } | null): BodyTopology | null {
  const t = rec?.params?.topology as BodyTopology | undefined | null
  if (!t || typeof t !== 'object' || !t.vertices || !t.edges || !t.faces) return null
  return t
}

function entityCount(t: BodyTopology): number {
  return Object.keys(t.vertices).length + Object.keys(t.edges).length + Object.keys(t.faces).length
}

// ---------------------------------------------------------------------------
// GROUPS + COMPONENTS (2026-09-27, Matt: "make selected sketch elements into groups
// and components which are not sticky with anything outside of themselves. Groups are
// one-off, and components behave like groups where if one component is changed, all
// components change at the same time … just like SketchUp").
//
// A group/component is a body element carrying params.bodyGroup = { kind, name,
// defId? }. ISOLATION: new drawing never sticks to it (drawBodySegments skips it)
// unless the drawing is made INSIDE its edit context (input.contextId), and edits
// never split it into pieces. COMPONENTS: every instance keeps its own WORLD topology
// (so every reader — render, pick, snap — is unchanged) plus params.bodyXform, the
// rigid local→world transform; the shared definition lives in the settings map
// (BODY_DEFS_KEY) in LOCAL coordinates. Editing any instance writes the definition
// from it and rewrites every other instance (propagateComponent); moving/rotating
// an instance as a whole only composes its own transform.
// ---------------------------------------------------------------------------
export const BODY_DEFS_KEY = 'bodyComponentDefs'
export interface BodyGroupInfo { kind: 'group' | 'component'; name: string; defId?: string }
/** 3×4 row-major rigid transform [r00 r01 r02 tx, r10 r11 r12 ty, r20 r21 r22 tz] */
export type BodyXform = number[]
const XF_IDENTITY: BodyXform = [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0]
const xfApply = (m: BodyXform, p: BodyPt3): BodyPt3 => ({
  x: m[0] * p.x + m[1] * p.y + m[2] * p.z + m[3],
  y: m[4] * p.x + m[5] * p.y + m[6] * p.z + m[7],
  z: m[8] * p.x + m[9] * p.y + m[10] * p.z + m[11],
})
function xfInvert(m: BodyXform): BodyXform {   // rigid (orthonormal R, or a reflection)
  const r = [m[0], m[4], m[8], m[1], m[5], m[9], m[2], m[6], m[10]]   // Rᵀ
  const t = [m[3], m[7], m[11]]
  return [
    r[0], r[1], r[2], -(r[0] * t[0] + r[1] * t[1] + r[2] * t[2]),
    r[3], r[4], r[5], -(r[3] * t[0] + r[4] * t[1] + r[5] * t[2]),
    r[6], r[7], r[8], -(r[6] * t[0] + r[7] * t[1] + r[8] * t[2]),
  ]
}
/** a ∘ b (apply b first) */
function xfCompose(a: BodyXform, b: BodyXform): BodyXform {
  const o: number[] = []
  for (let i = 0; i < 3; i++) {
    for (let j = 0; j < 3; j++) o.push(a[i * 4] * b[j] + a[i * 4 + 1] * b[4 + j] + a[i * 4 + 2] * b[8 + j])
    o.push(a[i * 4] * b[3] + a[i * 4 + 1] * b[7] + a[i * 4 + 2] * b[11] + a[i * 4 + 3])
  }
  return o
}
/** the affine matrix of a point map (sampled at the origin and the unit axes) */
function xfOfMap(map: (p: BodyPt3) => BodyPt3): BodyXform {
  const o = map({ x: 0, y: 0, z: 0 })
  const ex = map({ x: 1, y: 0, z: 0 }), ey = map({ x: 0, y: 1, z: 0 }), ez = map({ x: 0, y: 0, z: 1 })
  return [ex.x - o.x, ey.x - o.x, ez.x - o.x, o.x, ex.y - o.y, ey.y - o.y, ez.y - o.y, o.y, ex.z - o.z, ey.z - o.z, ez.z - o.z, o.z]
}
/** a topology mapped through a point map, face planes re-fit (exact for rigid maps) */
function mapTopology(t: BodyTopology, map: (p: BodyPt3) => BodyPt3): BodyTopology {
  const out = clone(t)
  for (const [vid, p] of Object.entries(out.vertices)) out.vertices[vid] = map(p)
  for (const f of Object.values(out.faces)) {
    const pts = f.loops[0].edges.map((eid, i) => {
      const e = out.edges[eid]
      return out.vertices[f.loops[0].dirs[i] ? e.a : e.b]
    })
    const pl = planeFromPoints(pts)
    if (pl) f.plane = pl
  }
  return out
}
export function bodyGroupOf(rec: { params: Record<string, unknown> } | null | undefined): BodyGroupInfo | null {
  const g = rec?.params?.bodyGroup as BodyGroupInfo | undefined
  return g && (g.kind === 'group' || g.kind === 'component') ? g : null
}
function readDefs(model: ModelDoc): Record<string, { name: string; topology: BodyTopology }> {
  return clone((maps(model).settings.get(BODY_DEFS_KEY) as Record<string, { name: string; topology: BodyTopology }>) || {})
}
/** the instances of a component definition */
export function componentInstances(model: ModelDoc, defId: string): ElementId[] {
  return listElements(model).filter((el) => el.category === 'body' && bodyGroupOf(el)?.defId === defId).map((el) => el.id)
}
// ---------------------------------------------------------------------------
// NESTING (2026-09-28, Matt: "Make groups and components nestable"). A group or
// component may sit INSIDE another: params.bodyParent = the containing group's id,
// params.bodyChildKey = a stable key naming that child within its parent's subtree
// (how a component's copies find each other's matching children). A group's OWN
// geometry is its topology (possibly empty once everything in it is sub-groups);
// its nested groups are the elements pointing at it.
// ---------------------------------------------------------------------------
export function bodyParentOf(rec: { params: Record<string, unknown> } | null | undefined): ElementId | null {
  const p = rec?.params?.bodyParent
  return typeof p === 'string' && p ? (p as ElementId) : null
}
const childKeyOf = (rec: { params: Record<string, unknown> } | null | undefined) => {
  const k = rec?.params?.bodyChildKey
  return typeof k === 'string' ? k : ''
}
/** the direct children of a body element */
export function bodyChildren(model: ModelDoc, id: ElementId): ElementId[] {
  return listElements(model).filter((el) => el.category === 'body' && bodyParentOf(el) === id).map((el) => el.id)
}
/** id and everything nested under it, parents before children */
export function bodySubtree(model: ModelDoc, id: ElementId): ElementId[] {
  const kids = new Map<ElementId, ElementId[]>()
  for (const el of listElements(model)) {
    if (el.category !== 'body') continue
    const p = bodyParentOf(el)
    if (!p) continue
    if (!kids.has(p)) kids.set(p, [])
    kids.get(p)!.push(el.id)
  }
  const out: ElementId[] = []
  const seen = new Set<ElementId>()
  const walk = (x: ElementId) => {
    if (seen.has(x)) return
    seen.add(x)
    out.push(x)
    for (const c of kids.get(x) || []) walk(c)
  }
  walk(id)
  return out
}
/** id, its parent, grandparent … up to the top */
export function bodyAncestry(model: ModelDoc, id: ElementId): ElementId[] {
  const out: ElementId[] = []
  let cur: ElementId | null = id
  while (cur && !out.includes(cur)) {
    out.push(cur)
    cur = bodyParentOf(getElement(model, cur))
  }
  return out
}
/**
 * What a click on body `id`'s geometry selects, with `ctx` the open group (or none):
 *   { id, editable: true }  — its edges/faces (loose geometry, or the open group's own)
 *   { id: G, editable: false } — a CLOSED group G, selected whole: the outermost group
 *                             outside any context, else the open group's child holding it
 *   null — outside the open group: not selectable (SketchUp)
 */
export function bodySelectTarget(model: ModelDoc, id: ElementId, ctx: ElementId | null): { id: ElementId; editable: boolean } | null {
  const chain = bodyAncestry(model, id)
  if (!ctx) {
    const top = chain[chain.length - 1]
    if (chain.length === 1 && !bodyGroupOf(getElement(model, top))) return { id: top, editable: true }
    return { id: top, editable: false }
  }
  const i = chain.indexOf(ctx)
  if (i < 0) return null
  if (i === 0) return { id: ctx, editable: true }
  return { id: chain[i - 1], editable: false }
}
const xfOf = (rec: { params: Record<string, unknown> } | null | undefined): BodyXform =>
  ((rec?.params?.bodyXform as BodyXform) || XF_IDENTITY)
/**
 * Make `dstRoot`'s nested contents match `srcRoot`'s, mapped through M (src world →
 * dst world): children are matched by bodyChildKey — updated, created, or removed.
 * Writes directly (never re-propagates). Call inside transact.
 */
function syncSubtree(model: ModelDoc, srcRoot: ElementId, dstRoot: ElementId, M: BodyXform): void {
  const mp = (p: BodyPt3) => xfApply(M, p)
  const src = bodySubtree(model, srcRoot).slice(1)
  const dstIds = bodySubtree(model, dstRoot).slice(1)
  const dstByKey = new Map<string, ElementId>()
  for (const id of dstIds) { const k = childKeyOf(getElement(model, id)); if (k) dstByKey.set(k, id) }
  const srcKeys = new Set<string>()
  const dstRootRec = getElement(model, dstRoot)
  for (const sid of src) {
    const srec = getElement(model, sid)
    if (!srec) continue
    const k = childKeyOf(srec)
    if (!k) continue
    srcKeys.add(k)
    const sp = bodyParentOf(srec)
    const parentKey = sp === srcRoot ? '' : (sp ? childKeyOf(getElement(model, sp)) : '')
    const dParent = parentKey === '' ? dstRoot : dstByKey.get(parentKey)
    if (!dParent) continue
    const g = bodyGroupOf(srec)
    const topo = mapTopology(topologyOf(srec) || createTopology(), mp)
    const fields: Record<string, unknown> = { topology: topo, bodyParent: dParent, bodyChildKey: k }
    if (g) fields.bodyGroup = clone(g)
    if (g?.kind === 'component') fields.bodyXform = xfCompose(M, xfOf(srec))
    if (typeof srec.params.bodyMaterial === 'string') fields.bodyMaterial = srec.params.bodyMaterial
    const did = dstByKey.get(k)
    if (did) {
      const pm = maps(model).elements.get(did)?.get('params') as Y.Map<unknown> | undefined
      if (pm) for (const [fk, fv] of Object.entries(fields)) pm.set(fk, fv)
    } else {
      const nid = newElementId()
      maps(model).elements.set(nid, recordToYMap({
        id: nid, category: 'body', typeId: srec.typeId, levelId: dstRootRec?.levelId ?? srec.levelId,
        ...(srec.phaseCreated ? { phaseCreated: srec.phaseCreated } : {}),
        ...(srec.phaseDemolished ? { phaseDemolished: srec.phaseDemolished } : {}),
        params: fields,
      }))
      dstByKey.set(k, nid)
    }
  }
  for (const [k, id] of dstByKey) if (!srcKeys.has(k)) maps(model).elements.delete(id)
}
/**
 * A change at `id` (its own geometry, or its nested contents) reaches every copy of
 * every component it is inside — innermost first, so an outer component copies the
 * already-updated inner ones. The definition's root topology is kept current too.
 */
function propagateComponents(model: ModelDoc, id: ElementId | null): void {
  if (!id) return
  for (const cid of bodyAncestry(model, id)) {
    const rec = getElement(model, cid)
    const g = bodyGroupOf(rec)
    if (!rec || g?.kind !== 'component' || !g.defId) continue
    const m = xfOf(rec)
    const inv = xfInvert(m)
    const local = mapTopology(topologyOf(rec) || createTopology(), (p) => xfApply(inv, p))
    const defs = readDefs(model)
    defs[g.defId] = { name: defs[g.defId]?.name || g.name, topology: local }
    maps(model).settings.set(BODY_DEFS_KEY, defs)
    for (const sid of componentInstances(model, g.defId)) {
      if (sid === cid) continue
      const srec = getElement(model, sid)
      const M = xfCompose(xfOf(srec), inv)
      const pm = maps(model).elements.get(sid)?.get('params') as Y.Map<unknown> | undefined
      if (pm) pm.set('topology', mapTopology(local, (p) => xfApply(xfOf(srec), p)))
      syncSubtree(model, cid, sid, M)
    }
  }
}
/** write a body's WORLD topology; inside a component (at any depth) the edit reaches
 *  every copy. Call inside transact. */
function writeTopology(model: ModelDoc, elementId: ElementId, topo: BodyTopology): void {
  const em = maps(model).elements.get(elementId)
  if (!em) return
  const pm = em.get('params') as Y.Map<unknown>
  pm.set('topology', topo)
  propagateComponents(model, elementId)
}

/** the one curated body type (name "Geometry" — SketchUp vocabulary, spike Q1);
 *  created on first use for models the seed hasn't reached yet */
export function ensureBodyTypeId(model: ModelDoc): TypeId {
  const existing = listTypes(model).find((t) => t.category === 'body')
  if (existing) return existing.id
  return createType(model, { category: 'body', name: 'Geometry', params: { material: '' } })
}

interface Seg3 { a: BodyPt3; b: BodyPt3 }

/** does any segment come within the weld tolerance of this topology — or lie ON
 *  one of its faces? (2026-09-26, Matt: a rectangle drawn in the middle of a box
 *  top "can't be subdivided" — it touched no edge or vertex, so it was minted as a
 *  separate body floating on the face instead of subdividing it. SketchUp sticks
 *  anything drawn on a face to that face.) */
function topologyTouches(topo: BodyTopology, segs: Seg3[]): boolean {
  for (const seg of segs) {
    const mid = { x: (seg.a.x + seg.b.x) / 2, y: (seg.a.y + seg.b.y) / 2, z: (seg.a.z + seg.b.z) / 2 }
    if (faceAt(topo, mid) && faceAt(topo, seg.a) && faceAt(topo, seg.b)) return true
    for (const p of Object.values(topo.vertices)) {
      if (pointSegDist(p, seg.a, seg.b).d <= WELD_TOL) return true
    }
    for (const e of Object.values(topo.edges)) {
      const pa = topo.vertices[e.a]
      const pb = topo.vertices[e.b]
      if (!pa || !pb) continue
      if (segSegClosest(seg.a, seg.b, pa, pb).d <= WELD_TOL) return true
    }
  }
  return false
}

export interface BodyDrawReport {
  elementId: ElementId
  addedEdges: number
  addedFaces: number
  /** elements the stickiness merge absorbed (deleted in the same transaction) */
  mergedFrom: ElementId[]
}

export function drawBodySegments(
  model: ModelDoc,
  input: { segments: Seg3[]; levelId: LevelId; phaseCreated?: PhaseId; contextId?: ElementId | null },
): BodyDrawReport | null {
  const finite = (p: BodyPt3) => Number.isFinite(p.x) && Number.isFinite(p.y) && Number.isFinite(p.z)
  const segs = input.segments.filter((s) => {
    if (!finite(s.a) || !finite(s.b)) return false // NaN guard — never write junk topology
    const dx = s.a.x - s.b.x, dy = s.a.y - s.b.y, dz = s.a.z - s.b.z
    return dx * dx + dy * dy + dz * dz > WELD_TOL * WELD_TOL
  })
  if (!segs.length) return null

  // --- pure computation first (reads only) -------------------------------
  // ISOLATION: groups/components never take part in stickiness from outside; inside
  // an edit context the drawing joins ONLY that group (touching or not)
  const ctxRec = input.contextId ? getElement(model, input.contextId) : null
  const inContext = !!(ctxRec && ctxRec.category === 'body' && bodyGroupOf(ctxRec))
  const bodies = listElements(model)
    .filter((el) => el.category === 'body' && (inContext ? el.id === input.contextId : !bodyGroupOf(el)))
    .map((el) => ({ id: el.id, topo: topologyOf(el) }))
    .filter((b): b is { id: ElementId; topo: BodyTopology } => !!b.topo)
  const touched = inContext
    ? bodies
    : bodies
      .filter((b) => topologyTouches(b.topo, segs))
      .sort((x, y) => entityCount(y.topo) - entityCount(x.topo))

  let topo: BodyTopology
  let targetId: ElementId | null = null
  const mergedFrom: ElementId[] = []
  if (!touched.length) {
    topo = createTopology()
  } else {
    targetId = touched[0].id
    topo = clone(touched[0].topo)
    for (const other of touched.slice(1)) {
      topo = mergeTopologies(topo, clone(other.topo))
      mergedFrom.push(other.id)
    }
  }
  let addedEdges = 0
  let addedFaces = 0
  for (const seg of segs) {
    const r = insertSegment(topo, seg.a, seg.b)
    addedEdges += r.addedEdges.length
    addedFaces += r.addedFaces.length
  }
  if (!Object.keys(topo.edges).length) return null
  if (entityCount(topo) > BODY_MAX_ENTITIES) return null // Q10 hard cap — refuse, never truncate

  // --- one transaction = one undo step -----------------------------------
  const report: BodyDrawReport = { elementId: targetId ?? ('' as ElementId), addedEdges, addedFaces, mergedFrom }
  transact(model, () => {
    for (const id of mergedFrom) maps(model).elements.delete(id)
    if (targetId) {
      writeTopology(model, targetId, topo)
    } else {
      const id = newElementId()
      maps(model).elements.set(id, recordToYMap({
        id,
        category: 'body',
        typeId: ensureBodyTypeId(model),
        levelId: input.levelId,
        ...(input.phaseCreated ? { phaseCreated: input.phaseCreated } : {}),
        params: { topology: topo },
      }))
      report.elementId = id
    }
  })
  return report
}

export interface BodyEraseReport {
  /** the body vanished entirely (its last edge went) */
  removedElement: boolean
  /** new elements minted when erasing disconnected the body (largest kept the id) */
  splitInto: ElementId[]
  removedFaces: number
}

export function eraseBodyEdge(model: ModelDoc, elementId: ElementId, eid: BodyEid): BodyEraseReport | null {
  return eraseBodyEdges(model, elementId, [eid])
}

/**
 * ERASER DRAG (2026-09-26, Matt: "click and hold and it highlights what I have
 * dragged over and will be deleted"): erase every listed edge of one body in a
 * single topology pass + a single transaction, then split disconnected pieces
 * once. Edges already gone (an earlier erase in the list took them) are skipped.
 * Callers erasing across several bodies wrap the calls in one `transact` so the
 * whole sweep is one undo step.
 */
export function eraseBodyEdges(model: ModelDoc, elementId: ElementId, eids: BodyEid[]): BodyEraseReport | null {
  const rec = getElement(model, elementId)
  if (!rec || rec.category !== 'body') return null
  if (rec.params.pinned === true) return null
  const stored = topologyOf(rec)
  if (!stored) return null
  const live = eids.filter((eid) => stored.edges[eid])
  if (!live.length) return null
  const topo = clone(stored)
  let removedCount = 0
  for (const eid of live) {
    if (!topo.edges[eid]) continue
    { const r = eraseEdge(topo, eid); removedCount += r.removedFaces.length - r.healedFaces.length }
  }

  const report: BodyEraseReport = { removedElement: false, splitInto: [], removedFaces: removedCount }
  transact(model, () => {
    // a group whose own geometry is all erased stays while it still holds nested groups
    if (!Object.keys(topo.edges).length && !bodyChildren(model, elementId).length) {
      maps(model).elements.delete(elementId)
      report.removedElement = true
      propagateComponents(model, bodyParentOf(rec))
      return
    }
    if (bodyGroupOf(rec)) { writeTopology(model, elementId, topo); return }   // a group stays one
    const comps = componentsOf(topo) // largest first
    const em = maps(model).elements.get(elementId)
    if (em) (em.get('params') as Y.Map<unknown>).set('topology', comps[0])
    for (const comp of comps.slice(1)) {
      const id = newElementId()
      maps(model).elements.set(id, recordToYMap({
        id,
        category: 'body',
        typeId: rec.typeId,
        levelId: rec.levelId,
        ...(rec.phaseCreated ? { phaseCreated: rec.phaseCreated } : {}),
        ...(rec.phaseDemolished ? { phaseDemolished: rec.phaseDemolished } : {}),
        params: { topology: comp },
      }))
      report.splitInto.push(id)
    }
  })
  return report
}

export interface PushPullReport {
  ok: boolean
  /** user-facing refusal (status bar shows it verbatim) when ok is false */
  error?: string
  mode?: 'out' | 'through' | 'move'
}

/**
 * PUSH/PULL (K2 — docs/bim-plan/21 §4.2): extrude a body face along its normal.
 * distFt is signed along the face's own +n; the kernel decides out/pocket/
 * through and refuses the recorded v1 cases with a user-facing message.
 * Ctrl (keepOriginal) leaves the source face in place — SketchUp's new-starting-
 * face. One call = one transaction = one undo step.
 */
export function pushPullBodyFace(
  model: ModelDoc,
  elementId: ElementId,
  fid: BodyFid,
  distFt: number,
  opts: { keepOriginal?: boolean } = {},
): PushPullReport {
  const rec = getElement(model, elementId)
  if (!rec || rec.category !== 'body') return { ok: false, error: 'Not a body.' }
  if (!Number.isFinite(distFt)) return { ok: false, error: 'Distance is not a number.' }
  if (rec.params.pinned === true) return { ok: false, error: 'This geometry is pinned.' }
  const stored = topologyOf(rec)
  if (!stored || !stored.faces[fid]) return { ok: false, error: 'No face to push/pull here.' }
  const topo = clone(stored)
  const r = extrudeFace(topo, fid, distFt, opts)
  if (!r.ok) return { ok: false, error: r.error }
  if (entityCount(topo) > BODY_MAX_ENTITIES) return { ok: false, error: 'This body is at its size limit.' }
  transact(model, () => { writeTopology(model, elementId, topo) })
  return { ok: true, mode: r.mode }
}

/** erase a face only — its edges stay (retrace one to heal it back) */
export function eraseBodyFace(model: ModelDoc, elementId: ElementId, fid: BodyFid): boolean {
  const rec = getElement(model, elementId)
  if (!rec || rec.category !== 'body') return false
  if (rec.params.pinned === true) return false
  const stored = topologyOf(rec)
  if (!stored || !stored.faces[fid]) return false
  const topo = clone(stored)
  eraseFace(topo, fid)
  transact(model, () => { writeTopology(model, elementId, topo) })
  return true
}

// ---------------------------------------------------------------------------
// MOVE / COPY / ROTATE (2026-09-27) — docs/MODELER.md "SKETCH MOVE/COPY/ROTATE"
// ---------------------------------------------------------------------------

export interface BodyPartsSel { edges: BodyEid[]; faces: BodyFid[] }
export interface BodyXformReport {
  ok: boolean; error?: string; elementIds?: ElementId[]
  /** a sub-selection copy: what it made, for the page to select (SketchUp selects the copy) */
  copied?: { elementId: ElementId; edges: string[]; faces: string[] }
}

/** write a topology back, splitting disconnected pieces into their own bodies
 *  (largest keeps the id — the eraseBodyEdges convention) */
function writeSplit(model: ModelDoc, elementId: ElementId, topo: BodyTopology): ElementId[] {
  const rec = getElement(model, elementId)!
  const ids: ElementId[] = [elementId]
  if (bodyGroupOf(rec)) { transact(model, () => writeTopology(model, elementId, topo)); return ids }
  const comps = componentsOf(topo)
  transact(model, () => {
    const em = maps(model).elements.get(elementId)
    if (em) (em.get('params') as Y.Map<unknown>).set('topology', comps[0] ?? topo)
    for (const comp of comps.slice(1)) {
      const id = newElementId()
      maps(model).elements.set(id, recordToYMap({
        id, category: 'body', typeId: rec.typeId, levelId: rec.levelId,
        ...(rec.phaseCreated ? { phaseCreated: rec.phaseCreated } : {}),
        ...(rec.phaseDemolished ? { phaseDemolished: rec.phaseDemolished } : {}),
        params: { topology: comp },
      }))
      ids.push(id)
    }
  })
  return ids
}

/**
 * Move (or rotate — any rigid `map`) sketch geometry. sel = a sub-selection (its
 * vertices move; attached geometry stretches — SketchUp's stickiness), or null for
 * the whole body. Refusals (a face bent out of flat, geometry through itself) leave
 * the model untouched. One call = one undo step.
 */
export function moveBodyParts(
  model: ModelDoc, elementId: ElementId, sel: BodyPartsSel | null, map: (p: BodyPt3) => BodyPt3,
): BodyXformReport {
  const rec = getElement(model, elementId)
  if (!rec || rec.category !== 'body') return { ok: false, error: 'Not sketch geometry.' }
  if (rec.params.pinned === true) return { ok: false, error: 'This geometry is pinned.' }
  const stored = topologyOf(rec)
  if (!stored) return { ok: false, error: 'No geometry.' }
  const topo = clone(stored)
  const vids = sel ? verticesOf(topo, sel.edges, sel.faces) : null
  if (vids && !vids.size) return { ok: false, error: 'Nothing selected to move.' }
  // a group holding only nested groups has no geometry of its own to check
  const r = !sel && bodyGroupOf(rec) && !Object.keys(topo.vertices).length ? { ok: true as const } : transformVertices(topo, vids, map)
  if (!r.ok) return { ok: false, error: r.error }
  const g = bodyGroupOf(rec)
  if (!sel && g) {
    // a group/component moved AS A WHOLE, with everything nested in it: only this
    // instance moves — a component composes its own transform, its definition (and
    // its siblings) untouched — unless it sits inside a component, whose copies follow
    const mx = xfOfMap(map)
    transact(model, () => {
      for (const id of bodySubtree(model, elementId)) {
        const r2 = getElement(model, id)
        const pm = maps(model).elements.get(id)?.get('params') as Y.Map<unknown> | undefined
        if (!r2 || !pm) continue
        pm.set('topology', id === elementId ? topo : mapTopology(topologyOf(r2) || createTopology(), map))
        if (bodyGroupOf(r2)?.kind === 'component') pm.set('bodyXform', xfCompose(mx, xfOf(r2)))
      }
      propagateComponents(model, bodyParentOf(rec))
    })
    return { ok: true, elementIds: [elementId] }
  }
  return { ok: true, elementIds: writeSplit(model, elementId, topo) }
}

/**
 * Copy sketch geometry through `map`. A sub-selection is copied INTO the same body
 * (it sticks to what it lands on; a copy landing free becomes its own body); a whole
 * body (sel null) is copied as a new body element.
 */
export function copyBodyParts(
  model: ModelDoc, elementId: ElementId, sel: BodyPartsSel | null, map: (p: BodyPt3) => BodyPt3,
): BodyXformReport {
  const rec = getElement(model, elementId)
  if (!rec || rec.category !== 'body') return { ok: false, error: 'Not sketch geometry.' }
  const stored = topologyOf(rec)
  if (!stored) return { ok: false, error: 'No geometry.' }
  if (!sel) {
    const topo = clone(stored)
    for (const [vid, p] of Object.entries(topo.vertices)) topo.vertices[vid] = map(p)
    for (const f of Object.values(topo.faces)) {
      const pts = f.loops[0].edges.map((eid, i) => {
        const e = topo.edges[eid]
        return topo.vertices[f.loops[0].dirs[i] ? e.a : e.b]
      })
      const pl = planeFromPoints(pts)
      if (pl) f.plane = pl
    }
    const id = newElementId()
    const g = bodyGroupOf(rec)
    const mx = xfOfMap(map)
    const extra: Record<string, unknown> = {}
    if (g) extra.bodyGroup = clone(g)
    if (g?.kind === 'component') extra.bodyXform = xfCompose(mx, xfOf(rec))
    const parent = bodyParentOf(rec)
    if (parent) { extra.bodyParent = parent; extra.bodyChildKey = 'k' + newElementId() }
    if (typeof rec.params.bodyMaterial === 'string') extra.bodyMaterial = rec.params.bodyMaterial
    transact(model, () => {
      maps(model).elements.set(id, recordToYMap({
        id, category: 'body', typeId: rec.typeId, levelId: rec.levelId,
        ...(rec.phaseCreated ? { phaseCreated: rec.phaseCreated } : {}),
        ...(rec.phaseDemolished ? { phaseDemolished: rec.phaseDemolished } : {}),
        params: { topology: topo, ...extra },
      }))
      // everything nested inside comes along (same keys — a copy is a sibling
      // instance, so a component copy's children match the original's)
      const idMap = new Map<ElementId, ElementId>([[elementId, id]])
      for (const sid of bodySubtree(model, elementId).slice(1)) {
        const srec = getElement(model, sid)
        const sp = bodyParentOf(srec)
        if (!srec || !sp || !idMap.has(sp)) continue
        const nid = newElementId()
        idMap.set(sid, nid)
        const sg = bodyGroupOf(srec)
        const fields: Record<string, unknown> = {
          topology: mapTopology(topologyOf(srec) || createTopology(), map),
          bodyParent: idMap.get(sp), bodyChildKey: childKeyOf(srec) || 'k' + newElementId(),
        }
        if (sg) fields.bodyGroup = clone(sg)
        if (sg?.kind === 'component') fields.bodyXform = xfCompose(mx, xfOf(srec))
        if (typeof srec.params.bodyMaterial === 'string') fields.bodyMaterial = srec.params.bodyMaterial
        maps(model).elements.set(nid, recordToYMap({
          id: nid, category: 'body', typeId: srec.typeId, levelId: srec.levelId,
          ...(srec.phaseCreated ? { phaseCreated: srec.phaseCreated } : {}),
        ...(srec.phaseDemolished ? { phaseDemolished: srec.phaseDemolished } : {}),
          params: fields,
        }))
      }
      propagateComponents(model, parent)
    })
    return { ok: true, elementIds: [id] }
  }
  const topo = clone(stored)
  const eids = edgesOf(topo, sel.edges, sel.faces)
  const r = copyEdges(topo, eids, map)
  if (!r.ok) return { ok: false, error: r.error }
  if (entityCount(topo) > BODY_MAX_ENTITIES) return { ok: false, error: 'This body is at its size limit.' }
  const ids = writeSplit(model, elementId, topo)
  // the copy may have split off into its own body — find the one holding its edges
  let copied: BodyXformReport['copied']
  if (r.edges.length) {
    for (const id of ids) {
      const t = topologyOf(getElement(model, id)!)
      if (t && r.edges.every((e) => t.edges[e])) { copied = { elementId: id, edges: r.edges, faces: r.faces.filter((f) => t.faces[f]) }; break }
    }
  }
  return { ok: true, elementIds: ids, ...(copied ? { copied } : {}) }
}

// ---------------------------------------------------------------------------
// MAKE GROUP / MAKE COMPONENT / EXPLODE / MAKE UNIQUE
// ---------------------------------------------------------------------------

export interface BodyGroupTarget { elementId: ElementId; sel: BodyPartsSel | null }

/** the sub-topology of a sub-selection: the selected faces (with their loops' edges)
 *  plus the selected edges, vertices copied */
function extractParts(t: BodyTopology, sel: BodyPartsSel): BodyTopology {
  const out = createTopology()
  const eids = edgesOf(t, sel.edges, sel.faces)
  for (const eid of eids) {
    const e = t.edges[eid]
    if (!e) continue
    out.edges[eid] = clone(e)
    out.vertices[e.a] = clone(t.vertices[e.a])
    out.vertices[e.b] = clone(t.vertices[e.b])
  }
  for (const fid of sel.faces) if (t.faces[fid]) out.faces[fid] = clone(t.faces[fid])
  return out
}
/** what remains of a body once a sub-selection leaves it: the selected faces go; a
 *  selected edge goes unless a remaining face still uses it (that edge stays on both
 *  sides — SketchUp duplicates the boundary into the group) */
function removeParts(t: BodyTopology, sel: BodyPartsSel): BodyTopology {
  const out = clone(t)
  for (const fid of sel.faces) delete out.faces[fid]
  const used = new Set<BodyEid>()
  for (const f of Object.values(out.faces)) for (const l of f.loops) for (const e of l.edges) used.add(e)
  for (const eid of edgesOf(t, sel.edges, sel.faces)) if (!used.has(eid)) delete out.edges[eid]
  const usedV = new Set<string>()
  for (const e of Object.values(out.edges)) { usedV.add(e.a); usedV.add(e.b) }
  for (const vid of Object.keys(out.vertices)) if (!usedV.has(vid)) delete out.vertices[vid]
  return out
}

/**
 * Wrap sketch geometry into ONE new group or component: a sub-selection of one body,
 * or whole loose bodies. The source keeps whatever wasn't selected. Nested groups
 * are not supported yet (a selection containing a group refuses). A component's
 * axes sit at the lower corner of its bounding box (SketchUp's default).
 */
export function makeBodyGroup(
  model: ModelDoc, targets: BodyGroupTarget[], kind: 'group' | 'component',
): { ok: boolean; error?: string; elementId?: ElementId } {
  if (!targets.length) return { ok: false, error: 'Select sketch geometry first.' }
  const recs = targets.map((t) => ({ t, rec: getElement(model, t.elementId) }))
  if (recs.some(({ rec }) => !rec || rec.category !== 'body')) return { ok: false, error: 'Only sketch geometry can be grouped.' }
  // NESTING: a whole selected group/component becomes a CHILD of the new group; loose
  // geometry (or a sub-selection of the open group's own geometry) becomes its own
  // geometry. Everything must share one context (the same parent, or none).
  const parentOfTarget = (t: BodyGroupTarget, rec: NonNullable<ReturnType<typeof getElement>>) =>
    t.sel && bodyGroupOf(rec) ? rec.id : bodyParentOf(rec)
  const parents = new Set(recs.map(({ t, rec }) => parentOfTarget(t, rec!) || ''))
  if (parents.size > 1) return { ok: false, error: 'Group things from one place at a time — they sit in different groups.' }
  const parentId = ([...parents][0] || null) as ElementId | null
  const childIds = recs.filter(({ t, rec }) => !t.sel && bodyGroupOf(rec)).map(({ rec }) => rec!.id)
  let world = createTopology()
  const leftovers: { id: ElementId; topo: BodyTopology | null; keep?: boolean }[] = []
  for (const { t, rec } of recs) {
    if (!t.sel && bodyGroupOf(rec)) continue            // a nested child — it stays itself
    const stored = topologyOf(rec!)
    if (!stored) continue
    if (t.sel) {
      world = mergeTopologies(world, extractParts(stored, t.sel))
      leftovers.push({ id: t.elementId, topo: removeParts(stored, t.sel), keep: !!bodyGroupOf(rec) })
    } else {
      world = mergeTopologies(world, clone(stored))
      leftovers.push({ id: t.elementId, topo: null })
    }
  }
  if (!Object.keys(world.edges).length && !childIds.length) return { ok: false, error: 'Nothing to group.' }
  const first = recs[0].rec!
  const id = newElementId()
  const defs = kind === 'component' ? readDefs(model) : null
  const n = kind === 'component' ? Object.keys(defs!).length + 1 : 0
  const name = kind === 'component' ? 'Component#' + n : 'Group'
  let params: Record<string, unknown> = { topology: world, bodyGroup: { kind, name } }
  if (kind === 'component') {
    // axes at the lower corner of EVERYTHING in it, nested groups included
    const vs = [...Object.values(world.vertices)]
    for (const cid of childIds) for (const sid of bodySubtree(model, cid)) vs.push(...Object.values(topologyOf(getElement(model, sid)!)?.vertices || {}))
    if (!vs.length) vs.push({ x: 0, y: 0, z: 0 })
    const o = { x: Math.min(...vs.map((v) => v.x)), y: Math.min(...vs.map((v) => v.y)), z: Math.min(...vs.map((v) => v.z)) }
    const defId = 'cd' + newElementId()
    const xf: BodyXform = [1, 0, 0, o.x, 0, 1, 0, o.y, 0, 0, 1, o.z]
    defs![defId] = { name, topology: mapTopology(world, (p) => ({ x: p.x - o.x, y: p.y - o.y, z: p.z - o.z })) }
    params = { topology: world, bodyGroup: { kind, name, defId }, bodyXform: xf }
  }
  if (parentId) { params.bodyParent = parentId; params.bodyChildKey = 'k' + newElementId() }
  transact(model, () => {
    for (const l of leftovers) {
      // an open group keeps its element even when its own geometry is all grouped away
      if (!l.keep && (!l.topo || !Object.keys(l.topo.edges).length)) { maps(model).elements.delete(l.id); continue }
      const em = maps(model).elements.get(l.id)
      if (em) (em.get('params') as Y.Map<unknown>).set('topology', l.topo || createTopology())
    }
    if (defs) maps(model).settings.set(BODY_DEFS_KEY, defs)
    maps(model).elements.set(id, recordToYMap({
      id, category: 'body', typeId: first.typeId, levelId: first.levelId,
      ...(first.phaseCreated ? { phaseCreated: first.phaseCreated } : {}),
      ...(first.phaseDemolished ? { phaseDemolished: first.phaseDemolished } : {}),
      params,
    }))
    for (const cid of childIds) {
      const pm = maps(model).elements.get(cid)?.get('params') as Y.Map<unknown> | undefined
      if (!pm) continue
      pm.set('bodyParent', id)
      if (!childKeyOf(getElement(model, cid))) pm.set('bodyChildKey', 'k' + newElementId())
    }
    propagateComponents(model, parentId)
  })
  return { ok: true, elementId: id }
}

/**
 * Explode a group/component: its contents go up ONE level (SketchUp) — nested groups
 * become children of its parent (or top-level), and its own geometry joins the
 * parent group's geometry (or becomes loose geometry at the top). Nothing moves.
 */
export function explodeBodyGroup(model: ModelDoc, elementId: ElementId): boolean {
  const rec = getElement(model, elementId)
  if (!rec || !bodyGroupOf(rec)) return false
  const parent = bodyParentOf(rec)
  const own = topologyOf(rec) || createTopology()
  transact(model, () => {
    for (const cid of bodyChildren(model, elementId)) {
      const pm = maps(model).elements.get(cid)?.get('params') as Y.Map<unknown> | undefined
      if (!pm) continue
      if (parent) pm.set('bodyParent', parent)
      else { pm.delete('bodyParent'); pm.delete('bodyChildKey') }
    }
    if (parent) {
      const prec = getElement(model, parent)
      const ppm = maps(model).elements.get(parent)?.get('params') as Y.Map<unknown> | undefined
      if (prec && ppm && Object.keys(own.edges).length) ppm.set('topology', mergeTopologies(clone(topologyOf(prec) || createTopology()), clone(own)))
      maps(model).elements.delete(elementId)
    } else if (!Object.keys(own.edges).length) {
      maps(model).elements.delete(elementId)
    } else {
      const pm = maps(model).elements.get(elementId)?.get('params') as Y.Map<unknown>
      pm.delete('bodyGroup')
      pm.delete('bodyXform')
    }
    propagateComponents(model, parent)
  })
  return true
}

/** delete a body and everything nested in it (one undo step with the caller's transact) */
export function deleteBodyTree(model: ModelDoc, elementId: ElementId): number {
  const rec = getElement(model, elementId)
  if (!rec || rec.category !== 'body') return 0
  const ids = bodySubtree(model, elementId)
  const parent = bodyParentOf(rec)
  transact(model, () => {
    for (const id of ids) maps(model).elements.delete(id)
    propagateComponents(model, parent)
  })
  return ids.length
}

/** a component instance gets its OWN definition (edits stop reaching its siblings) */
export function makeBodyComponentUnique(model: ModelDoc, elementId: ElementId): boolean {
  const rec = getElement(model, elementId)
  const g = bodyGroupOf(rec)
  if (!rec || g?.kind !== 'component' || !g.defId) return false
  const defs = readDefs(model)
  const src = defs[g.defId]
  if (!src) return false
  const defId = 'cd' + newElementId()
  const name = 'Component#' + (Object.keys(defs).length + 1)
  defs[defId] = { name, topology: clone(src.topology) }
  transact(model, () => {
    maps(model).settings.set(BODY_DEFS_KEY, defs)
    const pm = maps(model).elements.get(elementId)?.get('params') as Y.Map<unknown>
    pm.set('bodyGroup', { kind: 'component', name, defId })
  })
  return true
}

// ---------------------------------------------------------------------------
// OFFSET / FOLLOW ME (2026-09-27) — docs/MODELER.md "OFFSET + FOLLOW ME"
// ---------------------------------------------------------------------------

function editTopo(model: ModelDoc, elementId: ElementId, fn: (t: BodyTopology) => { ok: boolean; error?: string }): { ok: boolean; error?: string } {
  const rec = getElement(model, elementId)
  if (!rec || rec.category !== 'body') return { ok: false, error: 'Not sketch geometry.' }
  if (rec.params.pinned === true) return { ok: false, error: 'This geometry is pinned.' }
  const stored = topologyOf(rec)
  if (!stored) return { ok: false, error: 'No geometry.' }
  const topo = clone(stored)
  const r = fn(topo)
  if (!r.ok) return r
  if (entityCount(topo) > BODY_MAX_ENTITIES) return { ok: false, error: 'This body is at its size limit.' }
  transact(model, () => writeTopology(model, elementId, topo))
  return { ok: true }
}

// ---------------------------------------------------------------------------
// PAINT (K5 — docs/bim-plan/21 §4.5; Matt 2026-09-27)
// ---------------------------------------------------------------------------

/** paint sides of faces in one body (null = back to unpainted). One undo step;
 *  a component's edit reaches every instance through writeTopology, as in SketchUp */
export function paintBodyFaces(
  model: ModelDoc, elementId: ElementId, targets: { fid: BodyFid; side: 'front' | 'back' }[], materialId: string | null,
) {
  return editTopo(model, elementId, (t) => {
    let n = 0
    for (const { fid, side } of targets) if (paintFaceSide(t, fid, side, materialId)) n++
    return n ? { ok: true } : { ok: false, error: 'Nothing to paint.' }
  })
}

/** a group's / component instance's own material — shows on every unpainted side
 *  inside it (SketchUp: painting a closed group paints its default faces) */
export function setBodyMaterial(model: ModelDoc, elementId: ElementId, materialId: string | null) {
  const em = maps(model).elements.get(elementId)
  const rec = getElement(model, elementId)
  if (!em || !rec || rec.category !== 'body') return { ok: false, error: 'Not sketch geometry.' }
  transact(model, () => {
    const pm = em.get('params') as Y.Map<unknown>
    if (materialId) pm.set('bodyMaterial', materialId)
    else pm.delete('bodyMaterial')
    // a group nested in a component: its copies take the paint too
    propagateComponents(model, bodyParentOf(rec))
  })
  return { ok: true }
}

/** set (or reset, xf = null) one side's texture position */
export function setBodyFaceUv(
  model: ModelDoc, elementId: ElementId, fid: BodyFid, side: 'front' | 'back', xf: number[] | null,
) {
  return editTopo(model, elementId, (t) => {
    const f = t.faces[fid]
    if (!f) return { ok: false, error: 'That face is gone.' }
    const key = side === 'front' ? 'frontUv' : 'backUv'
    const ok = xf && xf.length === 6 && xf.every((x) => Number.isFinite(x))
    if (ok) f[key] = [...xf] as [number, number, number, number, number, number]
    else delete f[key]
    return { ok: true }
  })
}

/** SketchUp's Reverse Faces */
export function reverseBodyFaces(model: ModelDoc, elementId: ElementId, fids: BodyFid[]) {
  return editTopo(model, elementId, (t) => {
    let n = 0
    for (const fid of fids) if (reverseFaceInPlace(t, fid)) n++
    if (!n) return { ok: false, error: 'Nothing to reverse.' }
    const bad = invariants(t)
    return bad.length ? { ok: false, error: 'Those faces could not be reversed.' } : { ok: true }
  })
}

/** offset a face's perimeter (every loop) by d — d > 0 into the face, d < 0 out.
 *  Folded-over pieces are trimmed, SketchUp's default; keepOverlaps (Alt) keeps them */
export function offsetBodyFace(model: ModelDoc, elementId: ElementId, fid: BodyFid, d: number, opts: { keepOverlaps?: boolean } = {}) {
  return editTopo(model, elementId, (t) => {
    const segs = faceOffsetSegments(t, fid, d, !!opts.keepOverlaps)
    if (!segs) return { ok: false, error: 'That offset leaves nothing — try a smaller distance.' }
    return insertSegments(t, segs)
  })
}

/** offset a connected, coplanar edge chain by d toward the side `towardPt` is on */
export function offsetBodyChain(model: ModelDoc, elementId: ElementId, eids: BodyEid[], d: number, towardPt: BodyPt3, opts: { keepOverlaps?: boolean } = {}) {
  return editTopo(model, elementId, (t) => {
    const segs = chainOffsetSegments(t, eids, d, towardPt, !!opts.keepOverlaps)
    if (!segs) return { ok: false, error: 'Offset needs two or more connected edges in one plane, and a distance that leaves something.' }
    return insertSegments(t, segs)
  })
}

/** Follow Me: sweep face `fid` of body `elementId` along a path polyline */
export function followMeBody(model: ModelDoc, elementId: ElementId, fid: BodyFid, path: BodyPt3[], closed: boolean) {
  return editTopo(model, elementId, (t) => followMe(t, fid, path, closed))
}
